import type { JSX } from 'react';
import { CalendarOutlined, ClockCircleOutlined, EnvironmentOutlined, NodeIndexOutlined, RightOutlined, UserOutlined } from '@ant-design/icons';

import { Button, plural } from '../ds';
import type { MyOpeningTasksDto } from '../../lib/api/openingTasks';
import { fmtDate } from './utils';

/**
 * Тёмная карточка открытия (как тёмные карточки обучения): ИП · город,
 * адрес, этап «N из 17», куратор, дни на этапе, плановая дата открытия и
 * полоса из 17 этапов (пройден / сейчас / следующий / впереди). Кнопка
 * «Весь план открытия» ведёт в визард «Открытие точки» (/opening).
 */
export function OpeningHero({ data }: { data: MyOpeningTasksDto }): JSX.Element {
  const p = data.pipeline;
  const partnerName = data.partner?.name ?? 'Партнёр';
  if (!p) {
    return (
      <section className="fk-ot-hero" aria-label="Открытие точки">
        <div>
          <div className="fk-ot-eyebrow">Открытие точки</div>
          <div className="fk-ot-hero-title">{partnerName}</div>
        </div>
        <div className="fk-ot-hero-meta">
          <span>Карточку открытия заведёт куратор — тогда здесь появятся этапы и задачи. Переписка по помещениям уже доступна ниже.</span>
        </div>
      </section>
    );
  }
  const stages = data.stages;
  const total = p.stage.total || stages.length;
  const doneCount = stages.filter((s) => s.state === 'done').length;
  return (
    <section className="fk-ot-hero" aria-label="Открытие точки">
      <div className="fk-ot-hero-top">
        <div style={{ minWidth: 0 }}>
          <div className="fk-ot-eyebrow">Открытие точки</div>
          <div className="fk-ot-hero-title">
            {partnerName} · {p.city}
          </div>
        </div>
        <Button variant="soft" size="sm" to="/opening" iconRight={<RightOutlined />}>
          Весь план открытия
        </Button>
      </div>
      <div className="fk-ot-hero-meta">
        <span>
          <NodeIndexOutlined />
          Этап:&nbsp;<b>{p.stage.label}</b>&nbsp;·&nbsp;<span className="fk-ot-mono">{p.stage.index} из {total}</span>
        </span>
        {p.address ? (
          <span>
            <EnvironmentOutlined />
            {p.address}
          </span>
        ) : null}
        <span>
          <UserOutlined />
          Куратор:&nbsp;{p.curator?.name ?? 'назначается'}
        </span>
        {p.daysOnStage != null ? (
          <span>
            <ClockCircleOutlined />
            На этапе&nbsp;<span className="fk-ot-mono">{p.daysOnStage}</span>&nbsp;{plural(p.daysOnStage, 'день', 'дня', 'дней')}
          </span>
        ) : null}
        {p.plannedOpenAt ? (
          <span>
            <CalendarOutlined />
            План открытия&nbsp;<span className="fk-ot-mono">{fmtDate(p.plannedOpenAt)}</span>
          </span>
        ) : null}
      </div>
      {stages.length > 0 ? (
        <div className="fk-ot-strip" style={{ gridTemplateColumns: `repeat(${stages.length}, minmax(0, 1fr))` }} aria-hidden="true">
          {stages.map((s) => (
            <i key={s.code} data-state={s.state} title={s.label} />
          ))}
        </div>
      ) : null}
      <div className="fk-ot-legend">
        <span>
          Пройдено <b className="fk-ot-mono">{doneCount}</b> из {total}
        </span>
        <span>
          Сейчас <b>{p.stage.label}</b>
          {p.nextStage ? <> → дальше {p.nextStage.label}</> : null}
        </span>
      </div>
    </section>
  );
}
